import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { theme } from "src/assets/theme";
import WhiteCard from "src/shared/components/WhiteCard";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useMemo, useState } from "react";
import { PatientOrder, ProductCategories } from "src/api/types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import { useLatestOrder } from "src/context/LatestOrder";
import { Box, Collapse } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

type ShippingValues = {
  status: string;
  Icon: React.ElementType;
  title: string;
  sliderValue: number;
};

interface ShipmentTrackingProps {
  page: string;
}

const mapStatusToValue: Record<
  NonNullable<PatientOrder["status"]>,
  ShippingValues | null
> = {
  sent_to_pharmacy: {
    status: `patient-dashboard.shipmentTracking.sent_to_pharmacy.status`,
    Icon: AutorenewIcon,
    title: `patient-dashboard.shipmentTracking.sent_to_pharmacy.title`,
    sliderValue: 1,
  },

  shipped: {
    status: `patient-dashboard.shipmentTracking.shipped.status`,
    Icon: LocalShippingIcon,
    title: `patient-dashboard.shipmentTracking.shipped.title`,
    sliderValue: 50,
  },

  delivered: {
    status: `patient-dashboard.shipmentTracking.delivered.status`,
    Icon: VerifiedIcon,
    title: `patient-dashboard.shipmentTracking.delivered.title`,
    sliderValue: 100,
  },
  created: null,
  paid: null,
  unpaid: null,
  canceled: null,
  finalized: null,
  denied: null,
};

const ShipmentTracking = ({ page }: ShipmentTrackingProps) => {
  const isMobile = useIsMobile();
  const order = useLatestOrder((o) => o);
  const component = "shipmentTracking";
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);

  const medication = useMemo(() => {
    return order?.details.medications[0];
  }, [order?.details.medications]);

  const values = useMemo(() => {
    if (order == null) return null;

    return mapStatusToValue[order.status!];
  }, [order]);

  const isDelivered = useMemo(() => {
    if (order == null) return null;

    return order.status === "delivered";
  }, [order]);

  const productName = useMemo(() => {
    if (order == null) return null;

    if (["fhl", "mhl-ot", "ed-ot"].includes(order.product.slug)) {
      return medication?.display_name;
    }

    return order.product.name;
  }, [medication?.display_name, order]);

  const subtext = useMemo(() => {
    if (order == null) return null;

    if (order.product_category.name === ProductCategories.WeightLoss) {
      return t(`${page}.${component}.subtext`);
    }

    const medicationName = t(
      `medications.${medication?.display_name?.toLowerCase()}.capitalized`,
      medication?.display_name || "",
    );

    if (order.product_category.name === ProductCategories.MentalHealth) {
      return `${t(`payment-information.amh-ot.header`)} (${medicationName})`;
    }

    return medicationName;
  }, [medication?.display_name, order, page, t]);

  const collapseIn = useMemo(() => {
    if (order?.status !== "delivered") {
      return true;
    }

    return open;
  }, [open, order?.status]);

  if (
    order == null ||
    !["shipped", "delivered", "sent_to_pharmacy"].includes(
      order.status ?? "",
    ) ||
    values == null
  ) {
    return null;
  }

  return (
    <WhiteCard
      gap={2}
      sx={{
        paddingRight: 0,
      }}
    >
      <Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          pr={3}
          sx={{ cursor: "pointer" }}
        >
          <Typography variant={"h6"} fontWeight={600}>
            {t(`${page}.${component}.header`)}
          </Typography>
          {order.status === "delivered" ? (
            <Box
              sx={{ color: theme.palette.primary.main }}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? (
                <KeyboardArrowUp fontSize="large" />
              ) : (
                <KeyboardArrowDown fontSize="large" />
              )}
            </Box>
          ) : null}
        </Stack>
        <Typography fontWeight={600}>{t(values.title)}</Typography>
      </Stack>
      <Collapse in={collapseIn}>
        <Stack direction="row" width="100%" alignItems={"center"}>
          <Stack width="100%" gap={0}>
            <Typography>{subtext}</Typography>
            <Slider
              size={isMobile ? "small" : "medium"}
              defaultValue={values.sliderValue}
              disabled
              sx={{
                width: "100%",
                "&.Mui-disabled": {
                  color: isDelivered ? "#3BB273" : theme.palette.primary.main,
                },
              }}
            />
            <Stack direction="row" gap={0.5}>
              {
                <values.Icon
                  fontSize="small"
                  sx={{
                    color: isDelivered ? "#3BB273" : "black",
                  }}
                />
              }
              <Typography
                variant="body2"
                sx={{ color: isDelivered ? "#3BB273" : "black" }}
              >
                {`${t(values.status)}${order.tracking_number ? ":" : ""}`}
              </Typography>
              {order.tracking_number ? (
                <Link
                  to={order.tracking_url || ""}
                  target="_blank"
                  style={{
                    color: isDelivered ? "#3BB273" : "black",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  {order.tracking_number}
                </Link>
              ) : null}
            </Stack>
          </Stack>
          <MedicationIcon name={productName ?? ""} slug={order.product.slug} />
        </Stack>
      </Collapse>
    </WhiteCard>
  );
};

export default ShipmentTracking;
